import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Headphones, SlidersHorizontal, Volume2, VolumeX } from 'lucide-react';
import { Btn, PrototypeNotice, Tooltip } from '../ui';
import DrawerHeader from './DrawerHeader';
import { apiErrorMessage } from '../../api/errors';
import {
  AUDIO_EDIT_CAPABILITIES,
  audioEditApi,
  hasStoredAudioFile,
  needsStoredAudioFinalization,
  storedAudioRequirementMessage,
  type MixAnalysisTrack,
} from '../../api/audioEditApi';
import {
  EQ_BANDS,
  STEM_TRACKS,
  describePan,
  initialChannels,
  initialCompressorSettings,
  initialDelaySettings,
  initialEqGains,
  initialReverbSettings,
  isAudible,
} from '../../lib/stems';
import type { CompressorSettings, DelaySettings, EqGains, MixChannel, ReverbSettings } from '../../lib/stems';
import { playPreviewAudio, stopPreviewAudio, usePlayer } from '../../hooks/usePlayer';
import { c } from '../../styles/theme';
import type { Song, SongVersion } from '../../types/domain';

export interface MixPanelDraft {
  channels: Record<string, MixChannel>;
  analyzedChannels: Record<string, MixChannel> | null;
  eq: EqGains;
  analyzedEq: EqGains;
  compressor: CompressorSettings;
  analyzedCompressor: CompressorSettings;
  reverb: ReverbSettings;
  analyzedReverb: ReverbSettings;
  delay: DelaySettings;
  analyzedDelay: DelaySettings;
  sectionEnabled: Record<ToggleableSection, boolean>;
  stemsPresent: boolean;
  analysisError: string | null;
}

export interface MixPanelProps {
  song: Song;
  processing: boolean;
  onBack: () => void;
  onClose: () => void;
  onApplyMix: (input: {
    tracks?: Array<{ id: string; volume: number; pan: number; muted: boolean }>;
    eq: EqGains;
    compressor: CompressorSettings;
    reverb: ReverbSettings;
    delay: DelaySettings;
  }) => void;
  editingHeadVersion?: SongVersion | null;
  previewTransportFocused?: boolean;
  previewResumeElapsed?: number;
  onPreviewLoadingChange?: (loading: boolean) => void;
  sessionDraft?: MixPanelDraft | null;
  onSessionDraftChange?: (draft: MixPanelDraft) => void;
}

const LABEL: CSSProperties = { fontSize: 12, color: c.text2, margin: '0 0 8px' };
const DESC: CSSProperties = { fontSize: 12, color: c.text2, margin: '0 0 14px' };
const PREVIEW_DEBOUNCE_MS = 500;


function channelsFromAnalysis(tracks: MixAnalysisTrack[]): Record<string, MixChannel> {
  const next = initialChannels();
  for (const track of tracks) {
    if (!next[track.id]) continue;
    next[track.id] = {
      volume: track.volume,
      pan: track.pan,
      muted: track.muted,
      soloed: false,
    };
  }
  return next;
}

const EQ_RULER_SECTIONS = 25;

function formatEqGain(gain: number): string {
  const rounded = Math.round(gain * 10) / 10;
  return `${rounded > 0 ? '+' : ''}${rounded} dB`;
}

function EqSliderRuler({ bandId }: { bandId: string }) {
  return (
    <div
      aria-hidden="true"
      data-testid={`eq-ruler-${bandId}`}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${EQ_RULER_SECTIONS}, minmax(0, 1fr))`,
        alignItems: 'end',
        gap: 0,
        height: 10,
        marginTop: 4,
        paddingInline: 2,
      }}
    >
      {Array.from({ length: EQ_RULER_SECTIONS }, (_, index) => (
        <span
          key={index}
          data-testid="eq-ruler-tick"
          style={{
            borderLeft: index === 0 ? `1px solid ${c.border}` : undefined,
            borderRight: `1px solid ${c.border}`,
            height: index % 5 === 0 ? 10 : 6,
            opacity: index % 5 === 0 ? 1 : 0.75,
          }}
        />
      ))}
    </div>
  );
}

function formatCompressorMs(value: number): string {
  const rounded = value < 10 ? Math.round(value * 10) / 10 : Math.round(value);
  return `${rounded} ms`;
}

function formatCompressorDb(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return `${rounded > 0 ? '+' : ''}${rounded} dB`;
}

function formatRatio(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return `${rounded}:1`;
}

function formatReverbDelay(value: number): string {
  return `${Math.round(value)} ms`;
}

function formatReverbPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function formatDelayMs(value: number): string {
  return `${Math.round(value)} ms`;
}

function formatDelayMix(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function compressorOutputAtZeroDb(settings: CompressorSettings): number {
  return Math.round((settings.thresholdDb - settings.thresholdDb / settings.ratio) * 10) / 10;
}

function channelsMatch(left: Record<string, MixChannel>, right: Record<string, MixChannel>): boolean {
  return STEM_TRACKS.every((track) => {
    const leftChannel = left[track.id];
    const rightChannel = right[track.id];
    return (
      leftChannel?.volume === rightChannel?.volume &&
      leftChannel?.pan === rightChannel?.pan &&
      leftChannel?.muted === rightChannel?.muted &&
      leftChannel?.soloed === rightChannel?.soloed
    );
  });
}

function eqMatches(left: EqGains, right: EqGains): boolean {
  return EQ_BANDS.every((band) => left[band.id] === right[band.id]);
}

function compressorMatches(left: CompressorSettings, right: CompressorSettings): boolean {
  return (
    left.attackMs === right.attackMs &&
    left.releaseMs === right.releaseMs &&
    left.thresholdDb === right.thresholdDb &&
    left.ratio === right.ratio &&
    left.makeupGainDb === right.makeupGainDb
  );
}

function reverbMatches(left: ReverbSettings, right: ReverbSettings): boolean {
  return (
    left.delayMs === right.delayMs &&
    left.feedback === right.feedback &&
    left.dryVolume === right.dryVolume &&
    left.wetVolume === right.wetVolume
  );
}

function delayMatches(left: DelaySettings, right: DelaySettings): boolean {
  return left.delayMs === right.delayMs && left.feedback === right.feedback && left.mix === right.mix;
}

type ToggleableSection = 'balance' | 'eq' | 'compressor' | 'reverb' | 'delay';

const INITIAL_SECTION_ENABLED: Record<ToggleableSection, boolean> = {
  balance: false,
  eq: false,
  compressor: false,
  reverb: false,
  delay: false,
};

const DISABLED_EQ = initialEqGains();
const DISABLED_COMPRESSOR = initialCompressorSettings();
const DISABLED_REVERB = initialReverbSettings();
const DISABLED_DELAY = initialDelaySettings();

function initialSectionEnabled(): Record<ToggleableSection, boolean> {
  return { ...INITIAL_SECTION_ENABLED };
}

function emptyMixPanelDraft(): MixPanelDraft {
  return {
    channels: initialChannels(),
    analyzedChannels: null,
    eq: initialEqGains(),
    analyzedEq: initialEqGains(),
    compressor: initialCompressorSettings(),
    analyzedCompressor: initialCompressorSettings(),
    reverb: initialReverbSettings(),
    analyzedReverb: initialReverbSettings(),
    delay: initialDelaySettings(),
    analyzedDelay: initialDelaySettings(),
    sectionEnabled: initialSectionEnabled(),
    stemsPresent: false,
    analysisError: null,
  };
}

function SectionHeader({
  title,
  enabled,
  onToggle,
  accent = false,
  disabled = false,
}: {
  title: string;
  enabled: boolean;
  onToggle: () => void;
  accent?: boolean;
  disabled?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        marginTop: 20,
      }}
    >
      <p style={{ ...LABEL, margin: 0, color: accent ? c.accent : c.text1 }}>{title}</p>
      <button
        type="button"
        onClick={!disabled ? onToggle : undefined}
        aria-pressed={enabled}
        aria-label={`Turn ${title} ${enabled ? 'off' : 'on'}`}
        disabled={disabled}
        className="lyx-mix-toggle"
        style={{
          minWidth: 44,
          padding: '6px 10px',
          borderRadius: 999,
          border: `1px solid ${enabled ? c.accentBorder : c.border}`,
          background: enabled ? c.accentSoft : 'transparent',
          color: enabled ? c.accent : c.text2,
          fontSize: 11.5,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {enabled ? 'On' : 'Off'}
      </button>
    </div>
  );
}

export default function MixPanel({
  song,
  processing,
  onBack,
  onClose,
  onApplyMix,
  editingHeadVersion = null,
  previewTransportFocused = true,
  previewResumeElapsed = 0,
  onPreviewLoadingChange,
  sessionDraft = null,
  onSessionDraftChange,
}: MixPanelProps) {
  const restoreFromSessionRef = useRef(sessionDraft !== null);
  const initialDraft = sessionDraft ?? emptyMixPanelDraft();
  const [channels, setChannels] = useState<Record<string, MixChannel>>(initialDraft.channels);
  const [analyzedChannels, setAnalyzedChannels] = useState<Record<string, MixChannel> | null>(initialDraft.analyzedChannels);
  const [eq, setEq] = useState<EqGains>(initialDraft.eq);
  const [analyzedEq, setAnalyzedEq] = useState<EqGains>(initialDraft.analyzedEq);
  const [compressor, setCompressor] = useState<CompressorSettings>(initialDraft.compressor);
  const [analyzedCompressor, setAnalyzedCompressor] = useState<CompressorSettings>(initialDraft.analyzedCompressor);
  const [reverb, setReverb] = useState<ReverbSettings>(initialDraft.reverb);
  const [analyzedReverb, setAnalyzedReverb] = useState<ReverbSettings>(initialDraft.analyzedReverb);
  const [delay, setDelay] = useState<DelaySettings>(initialDraft.delay);
  const [analyzedDelay, setAnalyzedDelay] = useState<DelaySettings>(initialDraft.analyzedDelay);
  const [sectionEnabled, setSectionEnabled] = useState<Record<ToggleableSection, boolean>>(initialDraft.sectionEnabled);
  const [submittingSection, setSubmittingSection] = useState<'balance' | 'eq' | 'compressor' | 'reverb' | 'delay' | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(initialDraft.analysisError);
  const [stemsPresent, setStemsPresent] = useState(initialDraft.stemsPresent);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewAbortRef = useRef<AbortController | null>(null);
  const previewTimeoutRef = useRef<number | null>(null);
  const activePreviewRequestKeyRef = useRef<string | null>(null);

  const player = usePlayer();
  const previewActive = player.previewSongId === song.id;
  const previewFocused = previewTransportFocused && previewActive;
  const capability = AUDIO_EDIT_CAPABILITIES.mix;

  useEffect(() => {
    onPreviewLoadingChange?.(previewTransportFocused && previewLoading);
  }, [onPreviewLoadingChange, previewLoading, previewTransportFocused]);
  const stemCapability = AUDIO_EDIT_CAPABILITIES.separateStems;
  const canMix = audioEditApi.supports('mix');
  const storedAudioActionSource = editingHeadVersion ?? song;
  const storedAudioStatusSource = {
    songOrigin: song.songOrigin,
    audioUrl: storedAudioActionSource.audioUrl,
  };
  const storedAudioReady = hasStoredAudioFile(storedAudioActionSource);
  const finalizingAudio = !storedAudioReady && needsStoredAudioFinalization(storedAudioStatusSource);
  const blockedByStoredAudio = canMix && !storedAudioReady;
  const storedAudioMessage = storedAudioRequirementMessage(storedAudioStatusSource, 'mix this song');
  const storedAudioNotice = finalizingAudio
    ? 'Finalizing audio… live preview and mix rendering will unlock automatically as soon as Lyrixa finishes storing it.'
    : storedAudioMessage;

  useEffect(() => {
    let cancelled = false;

    if (!canMix || !storedAudioReady) {
      setAnalyzing(false);
      setAnalysisError(null);
      setStemsPresent(false);
      setAnalyzedChannels(null);
      setChannels(initialChannels());
      setAnalyzedEq(initialEqGains());
      setEq(initialEqGains());
      setAnalyzedCompressor(initialCompressorSettings());
      setCompressor(initialCompressorSettings());
      setAnalyzedReverb(initialReverbSettings());
      setReverb(initialReverbSettings());
      setAnalyzedDelay(initialDelaySettings());
      setDelay(initialDelaySettings());
      setSectionEnabled(initialSectionEnabled());
      return () => {
        cancelled = true;
      };
    }

    setAnalyzing(true);
    setAnalysisError(null);
    void audioEditApi
      .getMixAnalysis(song.id, editingHeadVersion?.id ?? null)
      .then((result) => {
        if (cancelled) return;
        const balanceAvailable = result.tracks.length > 0;
        const analyzed = balanceAvailable ? channelsFromAnalysis(result.tracks) : initialChannels();
        const analyzedCompressor = result.compressor ?? initialCompressorSettings();
        const analyzedReverb = result.reverb ?? initialReverbSettings();
        const analyzedDelay = result.delay ?? initialDelaySettings();
        setAnalyzedChannels(analyzed);
        setAnalyzedEq(result.eq);
        setAnalyzedCompressor(analyzedCompressor);
        setAnalyzedReverb(analyzedReverb);
        setAnalyzedDelay(analyzedDelay);
        setStemsPresent(balanceAvailable);
        setAnalysisError(null);

        if (restoreFromSessionRef.current) return;

        setChannels(analyzed);
        setEq(result.eq);
        setCompressor(analyzedCompressor);
        setReverb(analyzedReverb);
        setDelay(analyzedDelay);
        setSectionEnabled(initialSectionEnabled());
      })
      .catch((error) => {
        if (cancelled) return;
        setStemsPresent(false);
        setAnalysisError(error instanceof Error ? error.message : 'Could not prepare this song for mixing.');

        if (restoreFromSessionRef.current) return;

        setAnalyzedChannels(null);
        setChannels(initialChannels());
        setAnalyzedEq(initialEqGains());
        setEq(initialEqGains());
        setAnalyzedCompressor(initialCompressorSettings());
        setCompressor(initialCompressorSettings());
        setAnalyzedReverb(initialReverbSettings());
        setReverb(initialReverbSettings());
        setAnalyzedDelay(initialDelaySettings());
        setDelay(initialDelaySettings());
        setSectionEnabled(initialSectionEnabled());
      })
      .finally(() => {
        if (!cancelled) setAnalyzing(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canMix, storedAudioReady, song.id, editingHeadVersion?.id]);

  useEffect(() => {
    if (!processing) setSubmittingSection(null);
  }, [processing]);

  useEffect(() => {
    onSessionDraftChange?.({
      channels,
      analyzedChannels,
      eq,
      analyzedEq,
      compressor,
      analyzedCompressor,
      reverb,
      analyzedReverb,
      delay,
      analyzedDelay,
      sectionEnabled,
      stemsPresent,
      analysisError,
    });
  }, [
    analyzedChannels,
    analyzedCompressor,
    analyzedDelay,
    analyzedEq,
    analyzedReverb,
    analysisError,
    channels,
    compressor,
    delay,
    eq,
    onSessionDraftChange,
    reverb,
    sectionEnabled,
    stemsPresent,
  ]);

  const controlsDisabled = analyzing || processing || blockedByStoredAudio;
  const balanceControlsDisabled = controlsDisabled || !stemsPresent || !sectionEnabled.balance;

  const tracksForSubmit = useMemo(
    () =>
      STEM_TRACKS.map((track) => ({
        id: track.id,
        volume: channels[track.id]?.volume ?? 0,
        pan: channels[track.id]?.pan ?? 0,
        muted: !isAudible(track.id, channels),
      })),
    [channels]
  );
  const balanceRequest = useMemo(
    () => (stemsPresent && sectionEnabled.balance ? tracksForSubmit : undefined),
    [sectionEnabled.balance, stemsPresent, tracksForSubmit]
  );
  const effectiveEq = sectionEnabled.eq ? eq : DISABLED_EQ;
  const effectiveCompressor = sectionEnabled.compressor ? compressor : DISABLED_COMPRESSOR;
  const effectiveReverb = sectionEnabled.reverb ? reverb : DISABLED_REVERB;
  const effectiveDelay = sectionEnabled.delay ? delay : DISABLED_DELAY;
  const previewDirty = useMemo(() => {
    const baselineChannels = analyzedChannels ?? initialChannels();
    const balanceDirty =
      stemsPresent && sectionEnabled.balance && !channelsMatch(channels, baselineChannels);
    return (
      balanceDirty ||
      !eqMatches(effectiveEq, analyzedEq) ||
      !compressorMatches(effectiveCompressor, analyzedCompressor) ||
      !reverbMatches(effectiveReverb, analyzedReverb) ||
      !delayMatches(effectiveDelay, analyzedDelay)
    );
  }, [
    analyzedChannels,
    analyzedCompressor,
    analyzedDelay,
    analyzedEq,
    analyzedReverb,
    channels,
    effectiveCompressor,
    effectiveDelay,
    effectiveEq,
    effectiveReverb,
    sectionEnabled.balance,
    stemsPresent,
  ]);
  const previewRequest = useMemo(
    () => ({
      songId: song.id,
      ...(balanceRequest ? { tracks: balanceRequest } : null),
      eq: effectiveEq,
      compressor: effectiveCompressor,
      reverb: effectiveReverb,
      delay: effectiveDelay,
      ...(editingHeadVersion?.id ? { sourceVersionId: editingHeadVersion.id } : null),
    }),
    [
      song.id,
      balanceRequest,
      effectiveEq,
      effectiveCompressor,
      effectiveReverb,
      effectiveDelay,
      editingHeadVersion?.id,
    ]
  );
  const previewRequestKey = useMemo(() => JSON.stringify(previewRequest), [previewRequest]);
  const sectionApplyDisabled = {
    balance: controlsDisabled || !stemsPresent || !sectionEnabled.balance,
    eq: controlsDisabled || !sectionEnabled.eq,
    compressor: controlsDisabled || !sectionEnabled.compressor,
    reverb: controlsDisabled || !sectionEnabled.reverb,
    delay: controlsDisabled || !sectionEnabled.delay,
  } satisfies Record<'balance' | 'eq' | 'compressor' | 'reverb' | 'delay', boolean>;
  const eqControlsDisabled = controlsDisabled || !sectionEnabled.eq;
  const compressorControlsDisabled = controlsDisabled || !sectionEnabled.compressor;
  const reverbControlsDisabled = controlsDisabled || !sectionEnabled.reverb;
  const delayControlsDisabled = controlsDisabled || !sectionEnabled.delay;

  useEffect(() => {
    if (previewTimeoutRef.current !== null) {
      window.clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = null;
    }
    previewAbortRef.current?.abort();
    previewAbortRef.current = null;

    if (!canMix || controlsDisabled || analysisError || !previewDirty) {
      if (!previewDirty) {
        setPreviewError(null);
        activePreviewRequestKeyRef.current = null;
      }
      setPreviewLoading(false);
      if (previewFocused || !previewActive) stopPreviewAudio();
      return;
    }

    const reusingActivePreview = previewActive && activePreviewRequestKeyRef.current === previewRequestKey;
    if (reusingActivePreview) {
      setPreviewError(null);
      setPreviewLoading(false);
      return;
    }

    setPreviewError(null);
    setPreviewLoading(true);
    if (previewFocused || !previewActive) stopPreviewAudio();
    previewTimeoutRef.current = window.setTimeout(() => {
      const controller = new AbortController();
      previewAbortRef.current = controller;
      void audioEditApi
        .previewMix(previewRequest, controller.signal)
        .then(async (result) => {
          if (controller.signal.aborted || previewAbortRef.current !== controller) return;
          setPreviewError(null);
          if (!previewTransportFocused) return;
          activePreviewRequestKeyRef.current = previewRequestKey;
          await playPreviewAudio(song.id, result.blob, {
            startAt: previewActive ? player.previewElapsed : previewResumeElapsed,
            paused: previewActive ? player.previewPaused : false,
          });
        })
        .catch((error) => {
          if (controller.signal.aborted || previewAbortRef.current !== controller) return;
          setPreviewError(apiErrorMessage(error, { preserveMessage: true }));
          if (previewFocused || !previewActive) stopPreviewAudio();
        })
        .finally(() => {
          if (previewAbortRef.current === controller) previewAbortRef.current = null;
          if (!controller.signal.aborted) setPreviewLoading(false);
        });
    // The current focus mode is part of the request lifecycle: if the transport is showing
    // the head song, a newly rendered preview should not hijack it until the user opts back in.
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      if (previewTimeoutRef.current !== null) {
        window.clearTimeout(previewTimeoutRef.current);
        previewTimeoutRef.current = null;
      }
      previewAbortRef.current?.abort();
      previewAbortRef.current = null;
      setPreviewLoading(false);
    };
  }, [
    analysisError,
    canMix,
    controlsDisabled,
    previewDirty,
    previewRequest,
    previewRequestKey,
    previewTransportFocused,
    previewActive,
    song.id,
    setPreviewError,
  ]);

  useEffect(() => {
    return () => {
      if (previewTimeoutRef.current !== null) {
        window.clearTimeout(previewTimeoutRef.current);
        previewTimeoutRef.current = null;
      }
      previewAbortRef.current?.abort();
      previewAbortRef.current = null;
      setPreviewLoading(false);
      stopPreviewAudio();
    };
  }, []);

  function applyButtonLabel(section: 'balance' | 'eq' | 'compressor' | 'reverb' | 'delay', title: string): string {
    if (processing && submittingSection === section) return `Applying ${title}…`;
    return `Apply ${title}`;
  }

  function submitCurrentMix(section: 'balance' | 'eq' | 'compressor' | 'reverb' | 'delay') {
    if (sectionApplyDisabled[section]) return;
    setSubmittingSection(section);
    if (section === 'balance') {
      onApplyMix({
        tracks: tracksForSubmit,
        eq: DISABLED_EQ,
        compressor: DISABLED_COMPRESSOR,
        reverb: DISABLED_REVERB,
        delay: DISABLED_DELAY,
      });
      return;
    }
    if (section === 'eq') {
      onApplyMix({
        eq: effectiveEq,
        compressor: DISABLED_COMPRESSOR,
        reverb: DISABLED_REVERB,
        delay: DISABLED_DELAY,
      });
      return;
    }
    if (section === 'compressor') {
      onApplyMix({
        eq: DISABLED_EQ,
        compressor: effectiveCompressor,
        reverb: DISABLED_REVERB,
        delay: DISABLED_DELAY,
      });
      return;
    }
    if (section === 'reverb') {
      onApplyMix({
        eq: DISABLED_EQ,
        compressor: DISABLED_COMPRESSOR,
        reverb: effectiveReverb,
        delay: DISABLED_DELAY,
      });
      return;
    }
    onApplyMix({
      eq: DISABLED_EQ,
      compressor: DISABLED_COMPRESSOR,
      reverb: DISABLED_REVERB,
      delay: effectiveDelay,
    });
  }

  function resetSection(section: 'balance' | 'eq' | 'compressor' | 'reverb' | 'delay') {
    if (controlsDisabled) return;
    if (section === 'balance') {
      setChannels(analyzedChannels ?? initialChannels());
      return;
    }
    if (section === 'eq') {
      setEq(analyzedEq);
      return;
    }
    if (section === 'compressor') {
      setCompressor(analyzedCompressor);
      return;
    }
    if (section === 'reverb') {
      setReverb(analyzedReverb);
      return;
    }
    setDelay(analyzedDelay);
  }

  function patch(id: string, change: Partial<MixChannel>) {
    if (balanceControlsDisabled) return;
    setChannels((prev) => ({ ...prev, [id]: { ...prev[id], ...change } }));
  }

  function patchEq(id: keyof EqGains, gain: number) {
    if (eqControlsDisabled) return;
    setEq((prev) => ({ ...prev, [id]: gain }));
  }

  function patchCompressor(id: keyof CompressorSettings, value: number) {
    if (compressorControlsDisabled) return;
    setCompressor((prev) => ({ ...prev, [id]: value }));
  }

  function patchReverb(id: keyof ReverbSettings, value: number) {
    if (reverbControlsDisabled) return;
    setReverb((prev) => ({ ...prev, [id]: value }));
  }

  function patchDelay(id: keyof DelaySettings, value: number) {
    if (delayControlsDisabled) return;
    setDelay((prev) => ({ ...prev, [id]: value }));
  }

  function toggleSection(section: ToggleableSection) {
    if (controlsDisabled) return;
    if (section === 'balance' && !stemsPresent) return;
    setSectionEnabled((prev) => ({ ...prev, [section]: !prev[section] }));
  }

  function strip(track: (typeof STEM_TRACKS)[number]) {
    const channel = channels[track.id];
    const audible = isAudible(track.id, channels);

    return (
      <div
        key={track.id}
        className="lyx-mix-strip"
        role="group"
        aria-label={`${track.label} channel`}
        style={{
          background: c.surface1,
          border: `1px solid ${c.border}`,
          opacity: balanceControlsDisabled ? 0.6 : audible ? 1 : 0.55,
        }}
      >
        <div className="lyx-mix-strip-head">
          <span style={{ fontSize: 12.5, color: c.text1, fontWeight: 500 }}>{track.label}</span>
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            <Tooltip label={`Mute ${track.label}`} placement="bottom">
              <button
                type="button"
                onClick={() => patch(track.id, { muted: !channel.muted })}
                aria-pressed={channel.muted}
                aria-label={`Mute ${track.label}`}
                disabled={balanceControlsDisabled}
                className="lyx-mix-toggle"
                style={{
                  border: `1px solid ${channel.muted ? c.accentBorder : c.border}`,
                  background: channel.muted ? c.accentSoft : 'transparent',
                  color: channel.muted ? c.accent : c.text2,
                  cursor: balanceControlsDisabled ? 'not-allowed' : 'pointer',
                  opacity: balanceControlsDisabled ? 0.6 : 1,
                }}
              >
                {channel.muted ? <VolumeX size={12} /> : <Volume2 size={12} />}
              </button>
            </Tooltip>
            <Tooltip label={`Solo ${track.label}`} placement="bottom">
              <button
                type="button"
                onClick={() => patch(track.id, { soloed: !channel.soloed })}
                aria-pressed={channel.soloed}
                aria-label={`Solo ${track.label}`}
                disabled={balanceControlsDisabled}
                className="lyx-mix-toggle"
                style={{
                  border: `1px solid ${channel.soloed ? c.accentBorder : c.border}`,
                  background: channel.soloed ? c.accentSoft : 'transparent',
                  color: channel.soloed ? c.accent : c.text2,
                  cursor: balanceControlsDisabled ? 'not-allowed' : 'pointer',
                  opacity: balanceControlsDisabled ? 0.6 : 1,
                }}
              >
                <Headphones size={12} />
              </button>
            </Tooltip>
          </div>
        </div>

        <div className="lyx-mix-row">
          <span className="lyx-mix-row-label" style={{ color: c.text2 }}>
            Level
          </span>
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={Math.round(channel.volume * 100)}
            onChange={(e) => patch(track.id, { volume: Number(e.target.value) / 100 })}
            aria-label={`${track.label} level`}
            disabled={balanceControlsDisabled}
            className="lyx-tool-slider"
          />
          <span className="lyx-mix-row-value" style={{ color: c.text2 }}>
            {Math.round(channel.volume * 100)}%
          </span>
        </div>

        <div className="lyx-mix-row">
          <span className="lyx-mix-row-label" style={{ color: c.text2 }}>
            Pan
          </span>
          <input
            type="range"
            min={-100}
            max={100}
            step={1}
            value={Math.round(channel.pan * 100)}
            onChange={(e) => patch(track.id, { pan: Number(e.target.value) / 100 })}
            aria-label={`${track.label} pan`}
            aria-valuetext={describePan(channel.pan)}
            disabled={balanceControlsDisabled}
            className="lyx-tool-slider"
          />
          <span className="lyx-mix-row-value" style={{ color: c.text2 }}>
            {describePan(channel.pan)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="lyx-tool-panel">
      <DrawerHeader title="Mix" onBack={onBack} onClose={onClose} />
      <p style={DESC}>
        Balance works on separated stems. EQ, compression, reverb, and delay can run on the full
        song audio, and nothing is saved until you apply a section.
      </p>

      {editingHeadVersion && (
        <PrototypeNotice style={{ marginTop: 0, marginBottom: 14 }}>
          Editing from {editingHeadVersion.label}. New mixing will branch from this loaded head
          until another version is explicitly loaded.
        </PrototypeNotice>
      )}

      {!canMix && (
        <PrototypeNotice style={{ marginTop: 0, marginBottom: 14 }}>
          Nothing here is audible and nothing is charged. {stemCapability.missing} {capability.missing}
        </PrototypeNotice>
      )}

      {blockedByStoredAudio ? (
        <PrototypeNotice style={{ marginTop: 0, marginBottom: 14 }}>
          {storedAudioNotice}
        </PrototypeNotice>
      ) : analysisError && stemsPresent === false && canMix && !analyzing ? (
        <PrototypeNotice style={{ marginTop: 0, marginBottom: 14 }}>
          {analysisError}
        </PrototypeNotice>
      ) : null}

      {previewError && (
        <PrototypeNotice style={{ marginTop: 0, marginBottom: 14 }}>{previewError}</PrototypeNotice>
      )}

      {canMix && !blockedByStoredAudio && !analyzing && !analysisError && (
        <>
          <p style={{ ...DESC, marginTop: 0, marginBottom: 8 }}>
            Adjusting any enabled section plays a live preview only. Nothing is saved until you apply that section.
          </p>
          {(previewLoading || previewActive) && (
            <p style={{ ...DESC, marginTop: 0, marginBottom: 14, color: c.accent }}>
              {previewLoading ? 'Preview loading…' : 'Preview on'}
            </p>
          )}
        </>
      )}

      <SectionHeader
        title="Balance"
        enabled={sectionEnabled.balance}
        onToggle={() => toggleSection('balance')}
        disabled={controlsDisabled || !stemsPresent}
      />
      {!stemsPresent && (
        <p style={{ ...DESC, marginTop: 10, marginBottom: 0 }}>
          Split stems first to enable balance controls.
        </p>
      )}
      <div
        className="lyx-mix-strips"
        style={{ marginTop: 10, opacity: sectionEnabled.balance && stemsPresent ? 1 : 0.6 }}
      >
        {STEM_TRACKS.map(strip)}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
        <Btn
          variant="secondary"
          disabled={balanceControlsDisabled}
          onClick={!balanceControlsDisabled ? () => resetSection('balance') : undefined}
          aria-label="Reset balance"
        >
          <SlidersHorizontal size={13} />
          Reset balance
        </Btn>
        <Tooltip
          label={
            !stemsPresent
              ? 'Split stems first to apply balance.'
              : !sectionEnabled.balance
                ? 'Turn on Balance to apply these settings.'
                : 'Render this mix with the current level and pan settings.'
          }
        >
          <span>
            <Btn
              disabled={sectionApplyDisabled.balance}
              onClick={!sectionApplyDisabled.balance ? () => submitCurrentMix('balance') : undefined}
              aria-label={applyButtonLabel('balance', 'balance')}
            >
              <SlidersHorizontal size={13} />
              {applyButtonLabel('balance', 'balance')}
            </Btn>
          </span>
        </Tooltip>
      </div>

      <p style={{ ...LABEL, marginTop: 20 }}>Effects</p>
      <SectionHeader
        title="Equalizer"
        enabled={sectionEnabled.eq}
        onToggle={() => toggleSection('eq')}
        accent
        disabled={controlsDisabled}
      />
      <div
        aria-label="EQ controls"
        style={{
          display: 'grid',
          gap: 10,
          marginTop: 10,
          opacity: sectionEnabled.eq ? 1 : 0.6,
        }}
      >
        {EQ_BANDS.map((band) => (
          <div
            key={band.id}
            style={{
              background: c.surface1,
              border: `1px solid ${c.border}`,
              borderRadius: 14,
              padding: '12px 14px',
              display: 'grid',
              gap: 8,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
              <span style={{ fontSize: 12.5, color: c.text1, fontWeight: 500 }}>{band.label}</span>
              <span style={{ fontSize: 11.5, color: c.text2 }}>{band.frequencyHz.toLocaleString()} Hz</span>
            </div>
            <div className="lyx-mix-row" style={{ margin: 0, alignItems: 'center' }}>
              <span className="lyx-mix-row-label" style={{ color: c.text2 }}>
                Gain
              </span>
              <div style={{ minWidth: 0, width: '100%' }}>
                <input
                  type="range"
                  min={-12}
                  max={12}
                  step={0.5}
                  value={eq[band.id]}
                  onChange={(e) => patchEq(band.id, Number(e.target.value))}
                  aria-label={`${band.label} EQ`}
                  aria-valuetext={formatEqGain(eq[band.id])}
                  disabled={eqControlsDisabled}
                  className="lyx-tool-slider"
                />
                <EqSliderRuler bandId={band.id} />
              </div>
              <span className="lyx-mix-row-value" style={{ color: c.text2 }}>
                {formatEqGain(eq[band.id])}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10, gap: 8, flexWrap: 'wrap' }}>
        <Btn
          variant="secondary"
          disabled={eqControlsDisabled}
          onClick={!eqControlsDisabled ? () => resetSection('eq') : undefined}
          aria-label="Reset EQ"
        >
          <SlidersHorizontal size={13} />
          Reset EQ
        </Btn>
        <Tooltip
          label={
            !sectionEnabled.eq
              ? 'Turn on Equalizer to apply these settings.'
              : 'Render this mix with the current EQ settings.'
          }
        >
          <span>
            <Btn
              disabled={sectionApplyDisabled.eq}
              onClick={!sectionApplyDisabled.eq ? () => submitCurrentMix('eq') : undefined}
              aria-label={applyButtonLabel('eq', 'EQ')}
            >
              <SlidersHorizontal size={13} />
              {applyButtonLabel('eq', 'EQ')}
            </Btn>
          </span>
        </Tooltip>
      </div>

      <SectionHeader
        title="Compressor"
        enabled={sectionEnabled.compressor}
        onToggle={() => toggleSection('compressor')}
        disabled={controlsDisabled}
      />
      <div
        aria-label="Compressor controls"
        style={{
          display: 'grid',
          gap: 10,
          marginTop: 14,
          background: c.surface1,
          border: `1px solid ${c.border}`,
          borderRadius: 14,
          padding: '12px 14px',
          opacity: sectionEnabled.compressor ? 1 : 0.6,
        }}
      >
        <div style={{ display: 'grid', gap: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
            <span style={{ fontSize: 12.5, color: c.text1, fontWeight: 500 }}>Compressor</span>
            <span style={{ fontSize: 11.5, color: c.text2 }}>Broadband compand</span>
          </div>
          <p style={{ fontSize: 11.5, color: c.text2, margin: 0, lineHeight: 1.5 }}>
            Shapes dynamics after EQ. At 0 dB the current curve maps to {formatCompressorDb(compressorOutputAtZeroDb(compressor))}.
          </p>
        </div>

        <div className="lyx-mix-row" style={{ margin: 0 }}>
          <span className="lyx-mix-row-label" style={{ color: c.text2 }}>
            Attack
          </span>
          <input
            type="range"
            min={0.1}
            max={100}
            step={0.1}
            value={compressor.attackMs}
            onChange={(e) => patchCompressor('attackMs', Number(e.target.value))}
            aria-label="Compressor attack"
            aria-valuetext={formatCompressorMs(compressor.attackMs)}
            disabled={compressorControlsDisabled}
            className="lyx-tool-slider"
          />
          <span className="lyx-mix-row-value" style={{ color: c.text2 }}>
            {formatCompressorMs(compressor.attackMs)}
          </span>
        </div>

        <div className="lyx-mix-row" style={{ margin: 0 }}>
          <span className="lyx-mix-row-label" style={{ color: c.text2 }}>
            Release
          </span>
          <input
            type="range"
            min={50}
            max={1000}
            step={10}
            value={compressor.releaseMs}
            onChange={(e) => patchCompressor('releaseMs', Number(e.target.value))}
            aria-label="Compressor release"
            aria-valuetext={formatCompressorMs(compressor.releaseMs)}
            disabled={compressorControlsDisabled}
            className="lyx-tool-slider"
          />
          <span className="lyx-mix-row-value" style={{ color: c.text2 }}>
            {formatCompressorMs(compressor.releaseMs)}
          </span>
        </div>

        <div className="lyx-mix-row" style={{ margin: 0 }}>
          <span className="lyx-mix-row-label" style={{ color: c.text2 }}>
            Threshold
          </span>
          <input
            type="range"
            min={-60}
            max={0}
            step={0.5}
            value={compressor.thresholdDb}
            onChange={(e) => patchCompressor('thresholdDb', Number(e.target.value))}
            aria-label="Compressor threshold"
            aria-valuetext={formatCompressorDb(compressor.thresholdDb)}
            disabled={compressorControlsDisabled}
            className="lyx-tool-slider"
          />
          <span className="lyx-mix-row-value" style={{ color: c.text2 }}>
            {formatCompressorDb(compressor.thresholdDb)}
          </span>
        </div>

        <div className="lyx-mix-row" style={{ margin: 0 }}>
          <span className="lyx-mix-row-label" style={{ color: c.text2 }}>
            Ratio
          </span>
          <input
            type="range"
            min={1}
            max={20}
            step={0.1}
            value={compressor.ratio}
            onChange={(e) => patchCompressor('ratio', Number(e.target.value))}
            aria-label="Compressor ratio"
            aria-valuetext={formatRatio(compressor.ratio)}
            disabled={compressorControlsDisabled}
            className="lyx-tool-slider"
          />
          <span className="lyx-mix-row-value" style={{ color: c.text2 }}>
            {formatRatio(compressor.ratio)}
          </span>
        </div>

        <div className="lyx-mix-row" style={{ margin: 0 }}>
          <span className="lyx-mix-row-label" style={{ color: c.text2 }}>
            Makeup Gain
          </span>
          <input
            type="range"
            min={0}
            max={24}
            step={0.5}
            value={compressor.makeupGainDb}
            onChange={(e) => patchCompressor('makeupGainDb', Number(e.target.value))}
            aria-label="Compressor makeup gain"
            aria-valuetext={formatCompressorDb(compressor.makeupGainDb)}
            disabled={compressorControlsDisabled}
            className="lyx-tool-slider"
          />
          <span className="lyx-mix-row-value" style={{ color: c.text2 }}>
            {formatCompressorDb(compressor.makeupGainDb)}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10, gap: 8, flexWrap: 'wrap' }}>
        <Btn
          variant="secondary"
          disabled={compressorControlsDisabled}
          onClick={!compressorControlsDisabled ? () => resetSection('compressor') : undefined}
          aria-label="Reset compressor"
        >
          <SlidersHorizontal size={13} />
          Reset compressor
        </Btn>
        <Tooltip
          label={
            !sectionEnabled.compressor
              ? 'Turn on Compressor to apply these settings.'
              : 'Render this mix with the current compressor settings.'
          }
        >
          <span>
            <Btn
              disabled={sectionApplyDisabled.compressor}
              onClick={!sectionApplyDisabled.compressor ? () => submitCurrentMix('compressor') : undefined}
              aria-label={applyButtonLabel('compressor', 'compressor')}
            >
              <SlidersHorizontal size={13} />
              {applyButtonLabel('compressor', 'compressor')}
            </Btn>
          </span>
        </Tooltip>
      </div>

      <SectionHeader
        title="Reverb"
        enabled={sectionEnabled.reverb}
        onToggle={() => toggleSection('reverb')}
        disabled={controlsDisabled}
      />
      <div
        aria-label="Reverb controls"
        style={{
          display: 'grid',
          gap: 10,
          marginTop: 14,
          background: c.surface1,
          border: `1px solid ${c.border}`,
          borderRadius: 14,
          padding: '12px 14px',
          opacity: sectionEnabled.reverb ? 1 : 0.6,
        }}
      >
        <div style={{ display: 'grid', gap: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
            <span style={{ fontSize: 12.5, color: c.text1, fontWeight: 500 }}>Room reverb</span>
            <span style={{ fontSize: 11.5, color: c.text2 }}>FFmpeg aecho</span>
          </div>
          <p style={{ fontSize: 11.5, color: c.text2, margin: 0, lineHeight: 1.5 }}>
            Adds room depth after compression. Wet volume at 0% leaves the signal dry.
          </p>
        </div>

        <div className="lyx-mix-row" style={{ margin: 0 }}>
          <span className="lyx-mix-row-label" style={{ color: c.text2 }}>
            Room Size
          </span>
          <input
            type="range"
            min={10}
            max={250}
            step={1}
            value={reverb.delayMs}
            onChange={(e) => patchReverb('delayMs', Number(e.target.value))}
            aria-label="Reverb room size"
            aria-valuetext={formatReverbDelay(reverb.delayMs)}
            disabled={reverbControlsDisabled}
            className="lyx-tool-slider"
          />
          <span className="lyx-mix-row-value" style={{ color: c.text2 }}>
            {formatReverbDelay(reverb.delayMs)}
          </span>
        </div>

        <div className="lyx-mix-row" style={{ margin: 0 }}>
          <span className="lyx-mix-row-label" style={{ color: c.text2 }}>
            Feedback
          </span>
          <input
            type="range"
            min={0}
            max={0.7}
            step={0.01}
            value={reverb.feedback}
            onChange={(e) => patchReverb('feedback', Number(e.target.value))}
            aria-label="Reverb feedback"
            aria-valuetext={formatReverbPercent(reverb.feedback)}
            disabled={reverbControlsDisabled}
            className="lyx-tool-slider"
          />
          <span className="lyx-mix-row-value" style={{ color: c.text2 }}>
            {formatReverbPercent(reverb.feedback)}
          </span>
        </div>

        <div className="lyx-mix-row" style={{ margin: 0 }}>
          <span className="lyx-mix-row-label" style={{ color: c.text2 }}>
            Dry Volume
          </span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={reverb.dryVolume}
            onChange={(e) => patchReverb('dryVolume', Number(e.target.value))}
            aria-label="Reverb dry volume"
            aria-valuetext={formatReverbPercent(reverb.dryVolume)}
            disabled={reverbControlsDisabled}
            className="lyx-tool-slider"
          />
          <span className="lyx-mix-row-value" style={{ color: c.text2 }}>
            {formatReverbPercent(reverb.dryVolume)}
          </span>
        </div>

        <div className="lyx-mix-row" style={{ margin: 0 }}>
          <span className="lyx-mix-row-label" style={{ color: c.text2 }}>
            Wet Volume
          </span>
          <input
            type="range"
            min={0}
            max={0.5}
            step={0.01}
            value={reverb.wetVolume}
            onChange={(e) => patchReverb('wetVolume', Number(e.target.value))}
            aria-label="Reverb wet volume"
            aria-valuetext={formatReverbPercent(reverb.wetVolume)}
            disabled={reverbControlsDisabled}
            className="lyx-tool-slider"
          />
          <span className="lyx-mix-row-value" style={{ color: c.text2 }}>
            {formatReverbPercent(reverb.wetVolume)}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10, gap: 8, flexWrap: 'wrap' }}>
        <Btn
          variant="secondary"
          disabled={reverbControlsDisabled}
          onClick={!reverbControlsDisabled ? () => resetSection('reverb') : undefined}
          aria-label="Reset reverb"
        >
          <SlidersHorizontal size={13} />
          Reset reverb
        </Btn>
        <Tooltip
          label={
            !sectionEnabled.reverb
              ? 'Turn on Reverb to apply these settings.'
              : 'Render this mix with the current reverb settings.'
          }
        >
          <span>
            <Btn
              disabled={sectionApplyDisabled.reverb}
              onClick={!sectionApplyDisabled.reverb ? () => submitCurrentMix('reverb') : undefined}
              aria-label={applyButtonLabel('reverb', 'reverb')}
            >
              <SlidersHorizontal size={13} />
              {applyButtonLabel('reverb', 'reverb')}
            </Btn>
          </span>
        </Tooltip>
      </div>

      <SectionHeader
        title="Delay"
        enabled={sectionEnabled.delay}
        onToggle={() => toggleSection('delay')}
        disabled={controlsDisabled}
      />
      <div
        aria-label="Delay controls"
        style={{
          display: 'grid',
          gap: 10,
          marginTop: 14,
          background: c.surface1,
          border: `1px solid ${c.border}`,
          borderRadius: 14,
          padding: '12px 14px',
          opacity: sectionEnabled.delay ? 1 : 0.6,
        }}
      >
        <div style={{ display: 'grid', gap: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
            <span style={{ fontSize: 12.5, color: c.text1, fontWeight: 500 }}>Feedback delay</span>
            <span style={{ fontSize: 11.5, color: c.text2 }}>FFmpeg aecho</span>
          </div>
          <p style={{ fontSize: 11.5, color: c.text2, margin: 0, lineHeight: 1.5 }}>
            Adds a distinct echo after reverb. Mix cross-fades the dry and delayed signal.
          </p>
        </div>

        <div className="lyx-mix-row" style={{ margin: 0 }}>
          <span className="lyx-mix-row-label" style={{ color: c.text2 }}>
            Delay Time
          </span>
          <input
            type="range"
            min={100}
            max={1000}
            step={1}
            value={delay.delayMs}
            onChange={(e) => patchDelay('delayMs', Number(e.target.value))}
            aria-label="Delay time"
            aria-valuetext={formatDelayMs(delay.delayMs)}
            disabled={delayControlsDisabled}
            className="lyx-tool-slider"
          />
          <span className="lyx-mix-row-value" style={{ color: c.text2 }}>
            {formatDelayMs(delay.delayMs)}
          </span>
        </div>

        <div className="lyx-mix-row" style={{ margin: 0 }}>
          <span className="lyx-mix-row-label" style={{ color: c.text2 }}>
            Feedback
          </span>
          <input
            type="range"
            min={0}
            max={0.7}
            step={0.01}
            value={delay.feedback}
            onChange={(e) => patchDelay('feedback', Number(e.target.value))}
            aria-label="Delay feedback"
            aria-valuetext={formatReverbPercent(delay.feedback)}
            disabled={delayControlsDisabled}
            className="lyx-tool-slider"
          />
          <span className="lyx-mix-row-value" style={{ color: c.text2 }}>
            {formatReverbPercent(delay.feedback)}
          </span>
        </div>

        <div className="lyx-mix-row" style={{ margin: 0 }}>
          <span className="lyx-mix-row-label" style={{ color: c.text2 }}>
            Mix
          </span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={delay.mix}
            onChange={(e) => patchDelay('mix', Number(e.target.value))}
            aria-label="Delay mix"
            aria-valuetext={formatDelayMix(delay.mix)}
            disabled={delayControlsDisabled}
            className="lyx-tool-slider"
          />
          <span className="lyx-mix-row-value" style={{ color: c.text2 }}>
            {formatDelayMix(delay.mix)}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10, gap: 8, flexWrap: 'wrap' }}>
        <Btn
          variant="secondary"
          disabled={delayControlsDisabled}
          onClick={!delayControlsDisabled ? () => resetSection('delay') : undefined}
          aria-label="Reset delay"
        >
          <SlidersHorizontal size={13} />
          Reset delay
        </Btn>
        <Tooltip
          label={
            !sectionEnabled.delay
              ? 'Turn on Delay to apply these settings.'
              : 'Render this mix with the current delay settings.'
          }
        >
          <span>
            <Btn
              disabled={sectionApplyDisabled.delay}
              onClick={!sectionApplyDisabled.delay ? () => submitCurrentMix('delay') : undefined}
              aria-label={applyButtonLabel('delay', 'delay')}
            >
              <SlidersHorizontal size={13} />
              {applyButtonLabel('delay', 'delay')}
            </Btn>
          </span>
        </Tooltip>
      </div>

    </div>
  );
}
